import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useDispatch } from "react-redux";
import { getEducationDegreeLabel } from "../../utils/educationHelpers";
import { loadingActions } from "../../store/LoaderSlice";
import { modalActions } from "../../store/ModalSlice";
import {
  appendHrEditHistory,
  canHrDirectEditRequest,
  clearFlagForHrEdit,
  countEditedFields,
  getHrEditControl,
  HR_PINCODE_DEPENDENTS,
  hrRelatedFieldLabel,
  historyForField,
  isHrEditableField,
  isHrReplaceableDocument,
  classifyHrReplacementDocument,
  persistHrDirectEdit,
  persistHrDocumentReplace,
  readSavedRecord,
  readSectionValue,
  sanitizeHrFieldValue,
  upsertPendingHrEdits,
  validateHrEditReason,
  validateHrFieldEdit,
  validateReplacementFile,
  valuesAreSame,
  HR_CONFLICT_MESSAGE,
} from "../../utils/hrDirectEdit";

const applyValuesToForm = (formData, sectionKey, recordIndex, values) => {
  if (sectionKey === "education" || sectionKey === "employment") {
    const sectionData = formData?.[sectionKey] || {};
    if (recordIndex != null) {
      const records = [...(sectionData.records || [])];
      records[recordIndex] = {
        ...(records[recordIndex] || {}),
        ...values,
      };
      const nextSection = { ...sectionData, records };
      if (sectionKey === "education") {
        const ranked = records.find((item) => item?.highest_qualification);
        nextSection.highest_qualification =
          ranked?.qualification || records[0]?.qualification || "";
      }
      return { ...formData, [sectionKey]: nextSection };
    }
    return {
      ...formData,
      [sectionKey]: { ...sectionData, ...values },
    };
  }

  return {
    ...formData,
    [sectionKey]: {
      ...(formData?.[sectionKey] || {}),
      ...values,
    },
  };
};

const hasReturnedApprovalFeedback = (result) => {
  const feedback = result?.approval_feedback;
  return !!feedback && typeof feedback === "object" && !Array.isArray(feedback);
};

const showDocumentReplaceError = (dispatch, message) => {
  dispatch(
    modalActions.setErrorModal({
      message: message || "Document replacement failed",
      status: "error",
      state: true,
    }),
  );
};

const useHrDirectEdit = ({
  approvalStatus,
  queueStatus,
  formData,
  setFormData,
  setFieldFlags,
  rebuildFlags,
  onApprovalStatus,
  onPendingEditsChange,
  onDocumentReplaced,
  onVersionChange,
  onboardingId,
  requestVersion,
  requestId,
  editorName = "",
}) => {
  const dispatch = useDispatch();
  const enabled = canHrDirectEditRequest({ approvalStatus, queueStatus });
  const [session, setSession] = useState(null);
  const [history, setHistory] = useState([]);
  const [historyOpen, setHistoryOpen] = useState(false);
  const [pendingEdits, setPendingEdits] = useState([]);

  useEffect(() => {
    setSession(null);
    setHistory([]);
    setHistoryOpen(false);
    setPendingEdits([]);
    dispatch(loadingActions.setLoading(false));
  }, [requestId, dispatch]);

  const cancelEdit = useCallback(() => {
    setSession((current) => {
      if (current?.saving) return current;
      return null;
    });
  }, []);

  const startFieldEdit = useCallback(
    ({ sectionKey, fieldKey, recordIndex = null, label, isRequired }) => {
      if (!enabled || !isHrEditableField(sectionKey, fieldKey)) return;
      const saved = readSectionValue(formData, sectionKey, fieldKey, recordIndex);
      const values = { [fieldKey]: saved ?? "" };
      const savedValues = { [fieldKey]: saved ?? "" };
      if (fieldKey === "qualification") {
        const degree = readSectionValue(
          formData,
          sectionKey,
          "degree",
          recordIndex,
        );
        values.degree = degree ?? "";
        savedValues.degree = degree ?? "";
      }
      const pincodeDependents = HR_PINCODE_DEPENDENTS[fieldKey];
      if (pincodeDependents) {
        Object.values(pincodeDependents).forEach((dependentKey) => {
          const currentValue = readSectionValue(
            formData,
            sectionKey,
            dependentKey,
            recordIndex,
          );
          values[dependentKey] = currentValue ?? "";
          savedValues[dependentKey] = currentValue ?? "";
        });
      }
      if (
        sectionKey === "education" &&
        fieldKey !== "qualification" &&
        ["degree", "university", "college"].includes(fieldKey)
      ) {
        const qualification = readSectionValue(
          formData,
          sectionKey,
          "qualification",
          recordIndex,
        );
        values.qualification = qualification ?? "";
        savedValues.qualification = qualification ?? "";
      }
      setSession((current) => {
        // Block starting another edit while document replacement APIs are running
        if (current?.saving) return current;
        return {
          kind: "field",
          sectionKey,
          fieldKey,
          recordIndex,
          label,
          isRequired: !!isRequired,
          control: getHrEditControl(fieldKey),
          values,
          savedValues,
          reason: "",
          error: "",
        };
      });
    },
    [enabled, formData],
  );

  const updateDraftValue = useCallback((key, value) => {
    setSession((current) => {
      if (!current || current.kind !== "field") return current;
      const values = { ...current.values, [key]: value };
      if (key === "qualification" && !getEducationDegreeLabel(value)) {
        values.degree = "";
      }
      if (
        key === "qualification" &&
        getEducationDegreeLabel(value) &&
        !getEducationDegreeLabel(current.values.qualification)
      ) {
        values.degree = "";
      }
      return { ...current, values, error: "" };
    });
  }, []);

  const applyPincodeDetails = useCallback((fieldKey, pincode, details) => {
    const dependents = HR_PINCODE_DEPENDENTS[fieldKey];
    if (!dependents) return;
    const requested = String(pincode || "");
    setSession((current) => {
      if (!current || current.kind !== "field" || current.fieldKey !== fieldKey) {
        return current;
      }
      const currentPincode = String(current.values?.[fieldKey] ?? "").replace(/\D/g, "");
      if (currentPincode !== requested) return current;
      const nextCity = String(details?.city || "").trim();
      const nextState = String(details?.state || "").trim();
      return {
        ...current,
        values: {
          ...current.values,
          [dependents.city]: nextCity || current.values[dependents.city] || "",
          [dependents.state]: nextState || current.values[dependents.state] || "",
        },
      };
    });
  }, []);

  const updateReason = useCallback((reason) => {
    setSession((current) =>
      current && !current.saving
        ? { ...current, reason, error: "" }
        : current,
    );
  }, []);

  const startDocumentReplace = useCallback(
    ({ sectionKey, doc }) => {
      if (!enabled || !isHrReplaceableDocument(sectionKey, doc?.id)) return;
      setSession((current) => {
        if (current?.saving) return current;
        return {
          kind: "document",
          sectionKey,
          fieldKey: doc.id,
          recordIndex: null,
          label: doc.name || "Document",
          docId: doc.id,
          doc,
          file: null,
          fileName: "",
          reason: "",
          error: "",
          saving: false,
        };
      });
    },
    [enabled],
  );

  const replaceInFlightRef = useRef(false);

  const selectReplacementFile = useCallback((file) => {
    const fileError = validateReplacementFile(file);
    setSession((current) => {
      if (!current || current.kind !== "document" || current.saving) {
        return current;
      }
      if (fileError) {
        return { ...current, file: null, fileName: "", error: fileError };
      }
      return {
        ...current,
        file,
        fileName: file.name,
        error: "",
      };
    });
  }, []);

  const applySuccessfulEdit = useCallback(
    (result, targets, entries, formPatch, options = {}) => {
      const queuePendingEdits = options.queuePendingEdits !== false;

      if (formPatch) {
        setFormData((current) =>
          applyValuesToForm(
            current,
            formPatch.sectionKey,
            formPatch.recordIndex,
            formPatch.values,
          ),
        );
      }

      if (hasReturnedApprovalFeedback(result) && typeof rebuildFlags === "function") {
        setFieldFlags(rebuildFlags(result.approval_feedback));
      } else {
        setFieldFlags((current) =>
          (targets || []).reduce(
            (flags, target) => clearFlagForHrEdit(flags, target),
            current || {},
          ),
        );
      }

      if (entries?.length) {
        setHistory((current) => appendHrEditHistory(current, entries));
        // Document replace is already persisted via hr_replace_document —
        // do not queue it into save_progress.edits.
        if (queuePendingEdits) {
          setPendingEdits((current) => {
            const next = upsertPendingHrEdits(current, entries);
            onPendingEditsChange?.(next);
            return next;
          });
        }
      }

      const nextStatus = result?.approval_status;
      if (typeof nextStatus === "string" && nextStatus.trim()) {
        onApprovalStatus?.(nextStatus);
      }

      setSession(null);
    },
    [
      onApprovalStatus,
      onPendingEditsChange,
      rebuildFlags,
      setFieldFlags,
      setFormData,
    ],
  );

  const replaceDocumentFile = useCallback(
    async ({ sectionKey, doc, file }) => {
      if (!enabled || !isHrReplaceableDocument(sectionKey, doc?.id)) return;
      if (replaceInFlightRef.current) return;

      const fileError = validateReplacementFile(file);
      if (fileError) {
        showDocumentReplaceError(dispatch, fileError);
        return;
      }

      replaceInFlightRef.current = true;
      dispatch(loadingActions.setLoading(true));

      try {
        const classified = await classifyHrReplacementDocument({
          sectionKey,
          fieldKey: doc.id,
          file,
        });
        if (!classified?.ok) {
          showDocumentReplaceError(
            dispatch,
            classified?.message || "Document replacement failed",
          );
          return;
        }

        const personal = formData?.personal || {};
        const result = await persistHrDocumentReplace({
          dispatch,
          sectionKey,
          fieldKey: doc.id,
          docId: doc.id,
          doc,
          file,
          onboardingId,
          version: requestVersion,
          firstName: personal.first_name || "",
          lastName: personal.last_name || "",
        });
        if (!result?.ok) {
          showDocumentReplaceError(
            dispatch,
            result?.message || "Document replacement failed",
          );
          return;
        }

        if (
          result.version != null &&
          Number.isFinite(Number(result.version)) &&
          typeof onVersionChange === "function"
        ) {
          onVersionChange(Number(result.version));
        }

        if (typeof onDocumentReplaced === "function") {
          onDocumentReplaced({
            sectionKey,
            docId: doc.id,
            storageKey: result.storageKey,
            localFilename: result.localFilename || file.name,
          });
        }

        applySuccessfulEdit(
          result,
          [
            {
              sectionKey,
              fieldKey: doc.id,
              recordIndex: null,
            },
          ],
          [
            {
              sectionKey,
              fieldKey: doc.id,
              recordIndex: null,
              label: doc.name || "Document",
              oldValue: doc.name || "Document",
              newValue: result.localFilename || file.name || "",
              reason: "",
              editedBy: editorName || "HR",
              editedAt: new Date().toISOString(),
            },
          ],
          null,
          { queuePendingEdits: false },
        );
      } finally {
        replaceInFlightRef.current = false;
        dispatch(loadingActions.setLoading(false));
      }
    },
    [
      applySuccessfulEdit,
      dispatch,
      editorName,
      enabled,
      formData,
      onDocumentReplaced,
      onVersionChange,
      onboardingId,
      requestVersion,
    ],
  );

  const clearPendingEdits = useCallback(() => {
    setPendingEdits([]);
    onPendingEditsChange?.([]);
  }, [onPendingEditsChange]);

  const keepEditSessionError = useCallback((result) => {
    setSession((current) =>
      current
        ? {
            ...current,
            saving: false,
            error:
              result?.code === "CONFLICT"
                ? result.message || HR_CONFLICT_MESSAGE
                : result?.message || "This edit could not be saved.",
          }
        : current,
    );
  }, []);

  const saveEdit = useCallback(async () => {
    if (!session) return;
    if (session.saving) return;

    if (session.kind === "document") {
      const fileError = validateReplacementFile(session.file);
      if (fileError) {
        setSession((current) =>
          current ? { ...current, error: fileError, saving: false } : current,
        );
        return;
      }
      const reasonError = validateHrEditReason(session.reason);
      if (reasonError) {
        setSession((current) =>
          current ? { ...current, error: reasonError, saving: false } : current,
        );
        return;
      }

      setSession((current) =>
        current ? { ...current, saving: true, error: "" } : current,
      );
      // Common page loader — blocks all Review Request interaction until done
      dispatch(loadingActions.setLoading(true));

      try {
        const classified = await classifyHrReplacementDocument({
          sectionKey: session.sectionKey,
          fieldKey: session.fieldKey,
          file: session.file,
        });
        if (!classified?.ok) {
          keepEditSessionError(classified);
          return;
        }

        const personal = formData?.personal || {};
        const result = await persistHrDocumentReplace({
          dispatch,
          sectionKey: session.sectionKey,
          fieldKey: session.fieldKey,
          docId: session.docId,
          doc: session.doc,
          file: session.file,
          reason: String(session.reason || "").trim(),
          onboardingId,
          version: requestVersion,
          firstName: personal.first_name || "",
          lastName: personal.last_name || "",
        });
        if (!result?.ok) {
          keepEditSessionError(result);
          return;
        }

        if (
          result.version != null &&
          Number.isFinite(Number(result.version)) &&
          typeof onVersionChange === "function"
        ) {
          onVersionChange(Number(result.version));
        }

        if (typeof onDocumentReplaced === "function") {
          onDocumentReplaced({
            sectionKey: session.sectionKey,
            docId: session.docId,
            storageKey: result.storageKey,
            localFilename: result.localFilename || session.fileName,
          });
        }

        const editedAt = new Date().toISOString();
        applySuccessfulEdit(
          result,
          [
            {
              sectionKey: session.sectionKey,
              fieldKey: session.fieldKey,
              recordIndex: session.recordIndex,
            },
          ],
          [
            {
              sectionKey: session.sectionKey,
              fieldKey: session.fieldKey,
              recordIndex: session.recordIndex,
              label: session.label,
              oldValue: session.label,
              newValue: result.localFilename || session.fileName || "",
              reason: String(session.reason || "").trim(),
              editedBy: editorName || "HR",
              editedAt,
            },
          ],
          null,
          { queuePendingEdits: false },
        );
      } finally {
        dispatch(loadingActions.setLoading(false));
        setSession((current) =>
          current && current.kind === "document" && current.saving
            ? { ...current, saving: false }
            : current,
        );
      }
      return;
    }

    const savedRecord = {
      ...readSavedRecord(formData, session.sectionKey, session.recordIndex),
      ...session.savedValues,
    };
    const error = validateHrFieldEdit({
      sectionKey: session.sectionKey,
      fieldKey: session.fieldKey,
      label: session.label,
      isRequired: session.isRequired,
      values: session.values,
      savedRecord,
      reason: session.reason,
    });
    if (error) {
      setSession((current) => (current ? { ...current, error } : current));
      return;
    }

    const result = await persistHrDirectEdit({
      sectionKey: session.sectionKey,
      fieldKey: session.fieldKey,
      recordIndex: session.recordIndex,
      reason: String(session.reason || "").trim(),
    });
    if (!result?.ok) {
      keepEditSessionError(result);
      return;
    }

    const sanitized = {};
    Object.entries(session.values).forEach(([key, value]) => {
      sanitized[key] = sanitizeHrFieldValue(key, value);
    });
    const changedKeys = Object.keys(sanitized).filter(
      (key) => !valuesAreSame(key, savedRecord[key], sanitized[key]),
    );
    const editedAt = new Date().toISOString();
    applySuccessfulEdit(
      result,
      changedKeys.map((key) => ({
        sectionKey: session.sectionKey,
        fieldKey: key,
        recordIndex: session.recordIndex,
      })),
      changedKeys.map((key) => ({
        sectionKey: session.sectionKey,
        fieldKey: key,
        recordIndex: session.recordIndex,
        label:
          key === session.fieldKey
            ? session.label
            : hrRelatedFieldLabel(key, session.values?.qualification) || key,
        oldValue: savedRecord[key] ?? "",
        newValue: sanitized[key] ?? "",
        reason: String(session.reason || "").trim(),
        editedBy: editorName || "HR",
        editedAt,
      })),
      {
        sectionKey: session.sectionKey,
        recordIndex: session.recordIndex,
        values: sanitized,
      },
    );
  }, [
    applySuccessfulEdit,
    dispatch,
    editorName,
    formData,
    keepEditSessionError,
    onDocumentReplaced,
    onVersionChange,
    onboardingId,
    requestVersion,
    session,
  ]);

  return useMemo(
    () => ({
      enabled,
      session,
      history,
      historyOpen,
      pendingEdits,
      editedFieldCount: countEditedFields(history),
      setHistoryOpen,
      cancelEdit,
      startFieldEdit,
      updateDraftValue,
      applyPincodeDetails,
      updateReason,
      saveEdit,
      clearPendingEdits,
      startDocumentReplace,
      replaceDocumentFile,
      selectReplacementFile,
      isFieldEditable: (sectionKey, fieldKey) =>
        enabled && isHrEditableField(sectionKey, fieldKey),
      isDocumentReplaceable: (sectionKey, docId) =>
        enabled && isHrReplaceableDocument(sectionKey, docId),
      fieldHistory: (sectionKey, fieldKey, recordIndex) =>
        historyForField(history, sectionKey, fieldKey, recordIndex),
    }),
    [
      applyPincodeDetails,
      cancelEdit,
      clearPendingEdits,
      enabled,
      history,
      historyOpen,
      pendingEdits,
      saveEdit,
      replaceDocumentFile,
      selectReplacementFile,
      session,
      startDocumentReplace,
      startFieldEdit,
      updateDraftValue,
      updateReason,
    ],
  );
};

export default useHrDirectEdit;
